import { useMutation, useQuery, useQueryClient, UseQueryResult, UseMutationResult } from '@tanstack/react-query';
import { apiClient } from '../../utils/api-client';
import { CalendarSyncConfig } from '../../types';
import { calendarBookingsKeys } from './useCalendarBookings';

interface CalendarSyncStatusResponse {
  config: CalendarSyncConfig;
  lastSyncResult?: {
    syncedAt: string;
    bookingsFound: number;
    bookingsAdded: number;
    bookingsUpdated: number;
    errors?: string[];
  };
}

interface EnableCalendarSyncRequest {
  airbnbCalendarUrl: string;
  syncFrequency?: 'hourly' | 'daily' | 'manual';
}

interface ManualSyncResponse {
  syncId: string;
  status: 'started' | 'completed' | 'failed';
  message: string;
  progress?: {
    percentage: number;
    currentStep: string;
  };
}

// Query key factory for consistent cache management
export const calendarSyncKeys = {
  all: ['calendar-sync'] as const,
  statuses: () => [...calendarSyncKeys.all, 'status'] as const,
  status: (listingId: string) => [...calendarSyncKeys.statuses(), listingId] as const,
  syncs: () => [...calendarSyncKeys.all, 'sync'] as const,
  sync: (listingId: string) => [...calendarSyncKeys.syncs(), listingId] as const,
};

export const useCalendarSyncStatus = (
  listingId: string
): UseQueryResult<CalendarSyncStatusResponse, Error> => {
  return useQuery({
    queryKey: calendarSyncKeys.status(listingId),
    queryFn: async (): Promise<CalendarSyncStatusResponse> => {
      const response = await apiClient.get<CalendarSyncStatusResponse>(
        `/calendar-sync/${listingId}/status`
      );
      return response.data;
    },
    enabled: !!listingId,
    staleTime: 2 * 60 * 1000, // 2 minutes
    cacheTime: 5 * 60 * 1000, // 5 minutes
    refetchOnWindowFocus: false,
    retry: (failureCount, error) => {
      // Don't retry on 4xx errors, but retry on network errors
      if (error && 'status' in error && typeof error.status === 'number') {
        return error.status >= 500 && failureCount < 2;
      }
      return failureCount < 2;
    },
    retryDelay: (attemptIndex) => Math.min(1000 * 2 ** attemptIndex, 10000),
  });
};

interface UseEnableCalendarSyncReturn {
  mutate: (variables: { listingId: string; request: EnableCalendarSyncRequest }) => void;
  mutateAsync: (variables: { listingId: string; request: EnableCalendarSyncRequest }) => Promise<CalendarSyncStatusResponse>;
  isPending: boolean;
  isError: boolean;
  isSuccess: boolean;
  error: Error | null;
  data: CalendarSyncStatusResponse | undefined;
  enableWithOptimisticUpdate: (listingId: string, request: EnableCalendarSyncRequest) => Promise<CalendarSyncStatusResponse>;
}

export const useEnableCalendarSync = (): UseEnableCalendarSyncReturn => {
  const queryClient = useQueryClient();

  const mutation = useMutation({
    mutationFn: async ({
      listingId,
      request
    }: {
      listingId: string;
      request: EnableCalendarSyncRequest;
    }): Promise<CalendarSyncStatusResponse> => {
      const response = await apiClient.post<CalendarSyncStatusResponse>(
        `/calendar-sync/${listingId}/enable`,
        request
      );
      return response.data;
    },
    onMutate: async ({ listingId, request }) => {
      // Cancel any outgoing refetches
      await queryClient.cancelQueries({ queryKey: calendarSyncKeys.status(listingId) });

      // Snapshot the previous value
      const previousStatus = queryClient.getQueryData<CalendarSyncStatusResponse>(
        calendarSyncKeys.status(listingId)
      );

      // Optimistically update to the new value
      queryClient.setQueryData<CalendarSyncStatusResponse>(
        calendarSyncKeys.status(listingId),
        (old) => ({
          config: {
            listingId,
            enabled: true,
            airbnbCalendarUrl: request.airbnbCalendarUrl,
            syncStatus: 'syncing',
            syncFrequency: request.syncFrequency || 'daily',
            lastSyncAt: undefined,
            errorMessage: undefined,
          },
          lastSyncResult: old?.lastSyncResult,
        })
      );

      return { previousStatus };
    },
    onError: (err, { listingId }, context) => {
      // If the mutation fails, use the context returned from onMutate to roll back
      if (context?.previousStatus) {
        queryClient.setQueryData(calendarSyncKeys.status(listingId), context.previousStatus);
      }
    },
    onSuccess: (data, { listingId }) => {
      // Update the sync status with the actual response
      queryClient.setQueryData(calendarSyncKeys.status(listingId), data);
      // Invalidate calendar bookings to refresh external bookings
      queryClient.invalidateQueries({ queryKey: calendarBookingsKeys.all });
    },
    onSettled: (_, __, { listingId }) => {
      // Always refetch after error or success to ensure we have the latest data
      queryClient.invalidateQueries({ queryKey: calendarSyncKeys.status(listingId) });
    }
  });

  const enableWithOptimisticUpdate = async (listingId: string, request: EnableCalendarSyncRequest) => {
    return mutation.mutateAsync({ listingId, request });
  };

  return {
    mutate: mutation.mutate,
    mutateAsync: mutation.mutateAsync,
    isPending: mutation.isPending,
    isError: mutation.isError,
    isSuccess: mutation.isSuccess,
    error: mutation.error as Error | null,
    data: mutation.data,
    enableWithOptimisticUpdate,
  };
};

interface UseDisableCalendarSyncReturn {
  mutate: (variables: string) => void;
  mutateAsync: (variables: string) => Promise<CalendarSyncStatusResponse>;
  isPending: boolean;
  isError: boolean;
  isSuccess: boolean;
  error: Error | null;
  data: CalendarSyncStatusResponse | undefined;
  disableWithOptimisticUpdate: (listingId: string) => Promise<CalendarSyncStatusResponse>;
}

export const useDisableCalendarSync = (): UseDisableCalendarSyncReturn => {
  const queryClient = useQueryClient();

  const mutation = useMutation({
    mutationFn: async (listingId: string): Promise<CalendarSyncStatusResponse> => {
      const response = await apiClient.post<CalendarSyncStatusResponse>(
        `/calendar-sync/${listingId}/disable`
      );
      return response.data;
    },
    onMutate: async (listingId) => {
      // Cancel any outgoing refetches
      await queryClient.cancelQueries({ queryKey: calendarSyncKeys.status(listingId) });

      // Snapshot the previous value
      const previousStatus = queryClient.getQueryData<CalendarSyncStatusResponse>(
        calendarSyncKeys.status(listingId)
      );

      // Optimistically update to the new value
      queryClient.setQueryData<CalendarSyncStatusResponse>(
        calendarSyncKeys.status(listingId),
        (old) => ({
          config: {
            listingId,
            enabled: false,
            syncStatus: 'idle',
            syncFrequency: old?.config.syncFrequency || 'manual',
            airbnbCalendarUrl: old?.config.airbnbCalendarUrl,
            lastSyncAt: old?.config.lastSyncAt,
            errorMessage: undefined,
          },
          lastSyncResult: old?.lastSyncResult,
        })
      );

      return { previousStatus };
    },
    onError: (err, listingId, context) => {
      // If the mutation fails, use the context returned from onMutate to roll back
      if (context?.previousStatus) {
        queryClient.setQueryData(calendarSyncKeys.status(listingId), context.previousStatus);
      }
    },
    onSuccess: (data, listingId) => {
      // Update the sync status with the actual response
      queryClient.setQueryData(calendarSyncKeys.status(listingId), data);
      // Invalidate calendar bookings to refresh external bookings
      queryClient.invalidateQueries({ queryKey: calendarBookingsKeys.all });
    },
    onSettled: (_, __, listingId) => {
      // Always refetch after error or success to ensure we have the latest data
      queryClient.invalidateQueries({ queryKey: calendarSyncKeys.status(listingId) });
    }
  });

  const disableWithOptimisticUpdate = async (listingId: string) => {
    return mutation.mutateAsync(listingId);
  };

  return {
    mutate: mutation.mutate,
    mutateAsync: mutation.mutateAsync,
    isPending: mutation.isPending,
    isError: mutation.isError,
    isSuccess: mutation.isSuccess,
    error: mutation.error as Error | null,
    data: mutation.data,
    disableWithOptimisticUpdate,
  };
};

interface UseManualCalendarSyncReturn {
  mutate: (variables: { listingId: string; force?: boolean }) => void;
  mutateAsync: (variables: { listingId: string; force?: boolean }) => Promise<ManualSyncResponse>;
  isPending: boolean;
  isError: boolean;
  isSuccess: boolean;
  error: Error | null;
  data: ManualSyncResponse | undefined;
  syncWithOptimisticUpdate: (listingId: string, force?: boolean) => Promise<ManualSyncResponse>;
}

export const useManualCalendarSync = (): UseManualCalendarSyncReturn => {
  const queryClient = useQueryClient();

  const mutation = useMutation({
    mutationFn: async ({
      listingId,
      force = false
    }: {
      listingId: string;
      force?: boolean;
    }): Promise<ManualSyncResponse> => {
      const response = await apiClient.post<ManualSyncResponse>(
        `/calendar-sync/${listingId}/sync`,
        { force }
      );
      return response.data;
    },
    onMutate: async ({ listingId }) => {
      // Cancel any outgoing refetches
      await queryClient.cancelQueries({ queryKey: calendarSyncKeys.status(listingId) });

      // Snapshot the previous value
      const previousStatus = queryClient.getQueryData<CalendarSyncStatusResponse>(
        calendarSyncKeys.status(listingId)
      );

      // Optimistically update sync status to 'syncing'
      queryClient.setQueryData<CalendarSyncStatusResponse>(
        calendarSyncKeys.status(listingId),
        (old) => old ? {
          ...old,
          config: {
            ...old.config,
            syncStatus: 'syncing',
            errorMessage: undefined,
          }
        } : undefined
      );

      return { previousStatus };
    },
    onError: (err, { listingId }, context) => {
      // If the mutation fails, use the context returned from onMutate to roll back
      if (context?.previousStatus) {
        queryClient.setQueryData(calendarSyncKeys.status(listingId), context.previousStatus);
      }
    },
    onSuccess: (data, { listingId }) => {
      // Update sync status based on the response
      queryClient.setQueryData<CalendarSyncStatusResponse>(
        calendarSyncKeys.status(listingId),
        (old) => old ? {
          ...old,
          config: {
            ...old.config,
            syncStatus: data.status === 'completed' ? 'success' : 
                       data.status === 'failed' ? 'error' : 'syncing',
            lastSyncAt: data.status === 'completed' ? new Date().toISOString() : old.config.lastSyncAt,
            errorMessage: data.status === 'failed' ? data.message : undefined,
          }
        } : undefined
      );
      
      // Invalidate calendar bookings to refresh external bookings
      queryClient.invalidateQueries({ queryKey: calendarBookingsKeys.all });
    },
    onSettled: (_, __, { listingId }) => {
      // Always refetch after error or success to ensure we have the latest data
      queryClient.invalidateQueries({ queryKey: calendarSyncKeys.status(listingId) });
    }
  });

  const syncWithOptimisticUpdate = async (listingId: string, force?: boolean) => {
    return mutation.mutateAsync({ listingId, force });
  };

  return {
    mutate: mutation.mutate,
    mutateAsync: mutation.mutateAsync,
    isPending: mutation.isPending,
    isError: mutation.isError,
    isSuccess: mutation.isSuccess,
    error: mutation.error as Error | null,
    data: mutation.data,
    syncWithOptimisticUpdate,
  };
};

export default {
  useCalendarSyncStatus,
  useEnableCalendarSync,
  useDisableCalendarSync,
  useManualCalendarSync
};